import StaffChatApi from '../../api/staffChat';
import camelcaseKeys from 'camelcase-keys';

const state = {
  chats: [],
  selectedChatId: null,
  listLoading: false,
  listFetched: false,
  messagesLoading: false,
};

const getters = {
  getStaffChats: _state => _state.chats,
  getSelectedStaffChat: _state => {
    return _state.chats.find(chat => chat.id === _state.selectedChatId) || {};
  },
  getStaffChatListLoading: _state => _state.listLoading,
  getStaffMessagesLoading: _state => _state.messagesLoading,
};

const mutations = {
  SET_STAFF_CHATS(_state, chats) {
    _state.chats = chats;
  },
  MERGE_STAFF_CHATS(_state, chats) {
    const existingById = new Map(_state.chats.map(chat => [chat.id, chat]));
    _state.chats = chats.map(chat => {
      const existing = existingById.get(chat.id);
      if (!existing) return chat;

      let messages = existing.messages;
      if (!existing.dataFetched && chat.messages?.length) {
        messages = chat.messages;
      }

      return {
        ...chat,
        messages,
        dataFetched: existing.dataFetched,
      };
    });
    _state.chats.sort((a, b) => b.lastActivityAt - a.lastActivityAt);
  },
  SET_STAFF_CHAT_LIST_FETCHED(_state, status) {
    _state.listFetched = status;
  },
  SET_STAFF_CHAT_LIST_LOADING(_state, status) {
    _state.listLoading = status;
  },
  SET_STAFF_MESSAGES_LOADING(_state, status) {
    _state.messagesLoading = status;
  },
  SET_SELECTED_STAFF_CHAT(_state, chatId) {
    _state.selectedChatId = chatId;
  },
  CLEAR_SELECTED_STAFF_CHAT(_state) {
    _state.selectedChatId = null;
  },
  UPSERT_STAFF_CHAT(_state, chat) {
    const index = _state.chats.findIndex(c => c.id === chat.id);
    if (index === -1) {
      _state.chats = [chat, ..._state.chats];
      return;
    }
    const existing = _state.chats[index];
    _state.chats[index] = {
      ...existing,
      ...chat,
      messages: chat.messages?.length ? chat.messages : existing.messages,
    };
    _state.chats.sort((a, b) => b.lastActivityAt - a.lastActivityAt);
  },
  SET_STAFF_CHAT_MESSAGES(_state, { chatId, messages }) {
    const chat = _state.chats.find(c => c.id === chatId);
    if (chat) {
      chat.messages = messages;
      chat.dataFetched = true;
    }
  },
  ADD_STAFF_MESSAGE(_state, { chatId, message }) {
    const chat = _state.chats.find(c => c.id === chatId);
    if (!chat) return;

    const messages = chat.messages || [];
    const exists = messages.some(m => m.id === message.id);
    if (!exists) {
      chat.messages = [...messages, message];
    }
    chat.lastActivityAt = message.createdAt;
    _state.chats.sort((a, b) => b.lastActivityAt - a.lastActivityAt);
  },
  RESET_STAFF_CHAT_UNREAD(_state, chatId) {
    const chat = _state.chats.find(c => c.id === chatId);
    if (chat) {
      chat.unreadCount = 0;
    }
  },
  REMOVE_STAFF_CHAT(_state, chatId) {
    _state.chats = _state.chats.filter(c => c.id !== chatId);
    if (_state.selectedChatId === chatId) {
      _state.selectedChatId = null;
    }
  },
};

const actions = {
  fetchStaffChat: async ({ commit, state: moduleState }, chatId) => {
    if (moduleState.chats.find(c => c.id === chatId)) return;

    const { data } = await StaffChatApi.show(chatId);
    const chat = camelcaseKeys(data, { deep: true });
    commit('UPSERT_STAFF_CHAT', chat);
  },

  fetchStaffChats: async (
    { commit, state: moduleState },
    { force = false } = {}
  ) => {
    if (moduleState.listFetched && !force) return;

    const showLoading = !moduleState.listFetched;
    if (showLoading) commit('SET_STAFF_CHAT_LIST_LOADING', true);

    try {
      const { data } = await StaffChatApi.getAll();
      const chats = camelcaseKeys(data.data.payload, { deep: true });

      if (moduleState.listFetched) {
        commit('MERGE_STAFF_CHATS', chats);
      } else {
        commit('SET_STAFF_CHATS', chats);
      }

      commit('SET_STAFF_CHAT_LIST_FETCHED', true);
    } finally {
      if (showLoading) commit('SET_STAFF_CHAT_LIST_LOADING', false);
    }
  },

  createStaffChat: async ({ commit }, { userId }) => {
    const { data } = await StaffChatApi.create({ userId });
    const chat = camelcaseKeys(data, { deep: true });
    commit('UPSERT_STAFF_CHAT', chat);
    return chat;
  },

  createStaffGroupChat: async ({ commit }, { userIds, name }) => {
    const { data } = await StaffChatApi.createGroup({ userIds, name });
    const chat = camelcaseKeys(data, { deep: true });
    commit('UPSERT_STAFF_CHAT', chat);
    return chat;
  },

  fetchStaffChatMessages: async ({ commit, state: moduleState }, chatId) => {
    const chat = moduleState.chats.find(c => c.id === chatId);
    if (chat?.dataFetched) return;

    commit('SET_STAFF_MESSAGES_LOADING', true);
    try {
      const { data } = await StaffChatApi.getMessages(chatId);
      const messages = camelcaseKeys(data.payload, { deep: true });
      commit('SET_STAFF_CHAT_MESSAGES', { chatId, messages });
    } finally {
      commit('SET_STAFF_MESSAGES_LOADING', false);
    }
  },

  sendStaffMessage: async ({ commit }, { chatId, content, files = [] }) => {
    const { data } = await StaffChatApi.createMessage({
      staffChatId: chatId,
      content,
      files,
    });
    const message = camelcaseKeys(data, { deep: true });
    commit('ADD_STAFF_MESSAGE', { chatId, message });
    return message;
  },

  setActiveStaffChat: async ({ commit, dispatch }, chat) => {
    commit('SET_SELECTED_STAFF_CHAT', chat.id);
    await dispatch('fetchStaffChatMessages', chat.id);
    await StaffChatApi.updateLastSeen(chat.id);
    commit('RESET_STAFF_CHAT_UNREAD', chat.id);
  },

  clearActiveStaffChat({ commit }) {
    commit('CLEAR_SELECTED_STAFF_CHAT');
  },

  updateStaffGroupChat: async ({ commit }, { chatId, name }) => {
    const { data } = await StaffChatApi.update(chatId, { name });
    const chat = camelcaseKeys(data, { deep: true });
    commit('UPSERT_STAFF_CHAT', chat);
    return chat;
  },

  deleteStaffChat: async ({ commit }, chatId) => {
    await StaffChatApi.delete(chatId);
    commit('REMOVE_STAFF_CHAT', chatId);
  },

  handleStaffChatMessageCreated(
    { commit, dispatch, state: moduleState, rootGetters },
    data
  ) {
    const message = camelcaseKeys(data.staff_message, { deep: true });
    const chatId = message.staffChatId;
    const currentUserId = rootGetters.getCurrentUser?.id;
    const isOwnMessage = message.sender?.id === currentUserId;

    let chat = moduleState.chats.find(c => c.id === chatId);
    if (!chat) {
      dispatch('fetchStaffChats', { force: true });
      return;
    }

    commit('ADD_STAFF_MESSAGE', { chatId, message });
    if (data.last_activity_at) {
      commit('UPSERT_STAFF_CHAT', {
        ...chat,
        lastActivityAt: data.last_activity_at,
      });
    }

    if (moduleState.selectedChatId === chatId && !isOwnMessage) {
      StaffChatApi.updateLastSeen(chatId);
      commit('RESET_STAFF_CHAT_UNREAD', chatId);
    } else if (!isOwnMessage && moduleState.selectedChatId !== chatId) {
      chat = moduleState.chats.find(c => c.id === chatId);
      if (chat) {
        chat.unreadCount = (chat.unreadCount || 0) + 1;
      }
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
